import { useEffect, useRef } from 'react';
import { useLocation } from 'react-router';
import { toast } from 'sonner';

const CHECK_EVERY_MS = 5 * 60_000;
/** A tab coming back into focus checks again, but not more than this often. */
const MIN_GAP_MS = 60_000;

/**
 * The bundle this page loaded: the hashed filename of the entry chunk, read
 * off the <script> tag index.html shipped with. `/version.json` carries the
 * same filename for whatever is deployed now (vite.config.ts), so the two
 * differ exactly when the client code changed — not on every commit.
 */
function loadedBundle(): string | null {
  const src = document
    .querySelector<HTMLScriptElement>('script[type="module"][src]')
    ?.getAttribute('src');
  return src ? src.slice(src.lastIndexOf('/') + 1) : null;
}

/**
 * Tells an open tab when a newer build is deployed. Every deploy writes
 * `/version.json` next to the bundle; when it no longer names the bundle this
 * tab loaded, a toast offers a reload and the next client-side navigation
 * reloads on its own. It is a static asset, so the polling never touches the
 * API or the database.
 *
 * Reloading on navigation matters more than the toast: once a deploy has
 * replaced the hashed chunks, a lazy route in the old tab would request a
 * file that no longer exists, get index.html back from the SPA fallback, and
 * crash on the MIME type. `vite:preloadError` is the backstop for that race
 * when the poll has not run yet.
 *
 * Must render inside the router (it reads the location).
 */
export function UpdateNotice() {
  const { pathname } = useLocation();
  const stale = useRef(false);

  useEffect(() => {
    if (!stale.current) return;
    window.location.reload();
  }, [pathname]);

  useEffect(() => {
    if (import.meta.env.DEV) return;
    const current = loadedBundle();
    if (!current) return;

    let lastCheck = Date.now();

    const check = async () => {
      if (stale.current) return;
      lastCheck = Date.now();
      try {
        const res = await fetch('/version.json', { cache: 'no-store' });
        if (!res.ok) return;
        const { version } = (await res.json()) as { version?: string };
        if (!version || version === current) return;
        stale.current = true;
        toast('A new version of Billable is available', {
          id: 'app-update',
          duration: Infinity,
          action: { label: 'Update', onClick: () => window.location.reload() }
        });
      } catch {
        // Offline or mid-deploy; the next check will catch it.
      }
    };

    const onPreloadError = (event: Event) => {
      event.preventDefault();
      window.location.reload();
    };

    const timer = window.setInterval(() => void check(), CHECK_EVERY_MS);
    const onVisible = () => {
      if (document.visibilityState === 'visible' && Date.now() - lastCheck > MIN_GAP_MS) {
        void check();
      }
    };
    document.addEventListener('visibilitychange', onVisible);
    window.addEventListener('vite:preloadError', onPreloadError);
    return () => {
      window.clearInterval(timer);
      document.removeEventListener('visibilitychange', onVisible);
      window.removeEventListener('vite:preloadError', onPreloadError);
    };
  }, []);

  return null;
}
